import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  TrendingUp, Building2, Shield, Phone, Calendar, 
  ArrowRight, Users, Briefcase, Star, CheckCircle, BarChart3
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvestorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [topStartups, setTopStartups] = useState([]);
  const [myBookings, setMyBookings] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, startupsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/startups`)
      ]);
      setStats(statsRes.data);
      setTopStartups(startupsRes.data.slice(0, 6));
    } catch (e) {}
  };

  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <Header />
      
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[#C8A84B] to-[#D9BC6A] text-[#0D1B2A] py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="h-8 w-8" />
            <h1 className="text-3xl font-bold text-[#0D1B2A]">Welcome, {user?.full_name || 'Investor'}!</h1>
          </div>
          <p className="opacity-90">Your SKI AXIS Investor Dashboard</p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border border-[rgba(13,27,42,0.08)] text-[#0D1B2A]">
            <CardContent className="p-4 text-center">
              <Building2 className="h-8 w-8 text-[#C8A84B] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0D1B2A]">{stats.total_startups || 45}</p>
              <p className="text-sm text-[#7A8190]">Portfolio Startups</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[rgba(13,27,42,0.08)] text-[#0D1B2A]">
            <CardContent className="p-4 text-center">
              <Shield className="h-8 w-8 text-[#C8A84B] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0D1B2A]">87%</p>
              <p className="text-sm text-[#7A8190]">Compliance Rate</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[rgba(13,27,42,0.08)] text-[#0D1B2A]">
            <CardContent className="p-4 text-center">
              <BarChart3 className="h-8 w-8 text-[#C8A84B] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0D1B2A]">8.2</p>
              <p className="text-sm text-[#7A8190]">Avg Governance Score</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[rgba(13,27,42,0.08)] text-[#0D1B2A]">
            <CardContent className="p-4 text-center">
              <Users className="h-8 w-8 text-[#C8A84B] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0D1B2A]">20+</p>
              <p className="text-sm text-[#7A8190]">Jobs Created</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-white border border-[rgba(200,168,75,0.3)] hover:border-[rgba(200,168,75,0.4)] transition-all cursor-pointer text-[#0D1B2A]" onClick={() => navigate('/governance')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#C8A84B]/10 rounded-xl">
                  <Shield className="h-8 w-8 text-[#C8A84B]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#0D1B2A]">Governance Dashboard</h3>
                  <p className="text-sm text-[#7A8190]">View all startup metrics</p>
                </div>
              </div>
              <Button className="w-full mt-4 bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold">
                View Portfolio <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white border border-[rgba(200,168,75,0.3)] hover:border-[rgba(200,168,75,0.4)] transition-all cursor-pointer text-[#0D1B2A]" onClick={() => navigate('/pitch-day')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#C8A84B]/12 rounded-xl">
                  <Phone className="h-8 w-8 text-[#C8A84B]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#0D1B2A]">Pitch Day</h3>
                  <p className="text-sm text-[#7A8190]">Every Friday</p>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4 border-green-300 text-green-700">
                View Schedule <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white border border-[rgba(200,168,75,0.3)] hover:border-[rgba(200,168,75,0.4)] transition-all cursor-pointer text-[#0D1B2A]" onClick={() => navigate('/investor')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#C8A84B]/10 rounded-xl">
                  <Star className="h-8 w-8 text-[#C8A84B]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#0D1B2A]">My Profile</h3>
                  <p className="text-sm text-[#7A8190]">Update preferences</p>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4 border-purple-300 text-purple-700">
                Edit Profile <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Top Startups */}
        <Card className="mb-8 bg-white border border-[rgba(13,27,42,0.08)] text-[#0D1B2A]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-[#C8A84B]" />
                Featured Governance-Ready Startups
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => navigate('/governance')}>
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {topStartups.map((startup) => (
                <div key={startup.id} className="p-4 border rounded-lg hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-[#0D1B2A]">{startup.name}</h4>
                    <Badge className={startup.governance_score >= 8 ? 'bg-green-600' : 'bg-yellow-600'}>
                      {startup.governance_score}/10
                    </Badge>
                  </div>
                  <p className="text-sm text-[#7A8190] mb-2">{startup.industry}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{startup.ecosystem}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Ecosystems */}
        <Card className="bg-white border border-[rgba(13,27,42,0.08)] text-[#0D1B2A]">
          <CardHeader>
            <CardTitle>Investment Ecosystems</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-[#C8A84B]/8 rounded-lg border border-[rgba(200,168,75,0.2)]">
                <h4 className="font-semibold text-[#C8A84B] mb-2">Climate Tech</h4>
                <p className="text-sm text-[#0D1B2A]">15 startups</p>
                <p className="text-xs text-[#7A8190] mt-1">Solar, Water, Circular Economy, Carbon</p>
              </div>
              <div className="p-4 bg-[#C8A84B]/6 rounded-lg border border-[rgba(200,168,75,0.3)]">
                <h4 className="font-semibold text-[#C8A84B] mb-2">FinTech / RegTech</h4>
                <p className="text-sm text-[#0D1B2A]">15 startups</p>
                <p className="text-xs text-[#7A8190] mt-1">Payments, Compliance, InsurTech</p>
              </div>
              <div className="p-4 bg-[#C8A84B]/6 rounded-lg border border-[rgba(200,168,75,0.3)]">
                <h4 className="font-semibold text-[#C8A84B] mb-2">Cross-Border Services</h4>
                <p className="text-sm text-[#0D1B2A]">15 startups</p>
                <p className="text-xs text-[#7A8190] mt-1">HR Tech, EdTech, LegalTech</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvestorDashboard;
